import { FC, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Analytics from '@hh.ru/analytics-js';
import { translation, useSelector } from '@hh.ru/front-static-app';

import useAnalyticsEventShownOnce from 'src/hooks/useAnalyticsEventShownOnce';
import { setAdvicesVacancyAdvicesModal } from 'src/models/vacancyAdvicesModal';
import fetcher from 'src/modules/fetcher';
import prepareFormData from 'src/modules/fetcher/prepareFormData';
import { VacancyAdvice } from 'src/types/vacancyAdvices';

import FeedbackModalContent from 'src/components/VacancyAdvicesModal/FeedbackModalContent';
import VacancyAdviceCard from 'src/components/VacancyAdvicesModal/VacancyAdviceCard';
import {
    DEFAULT_FORM_VALUES,
    FormValues,
    HIDE_ADVICE_URL,
    Reason,
    VACANCY_ADVICE_MODAL_COMPONENT_EVENT,
    VacancyAdviceModalEventType,
} from 'src/components/VacancyAdvicesModal/constants';
import useProxyVacancyAdviceModalEvent from 'src/components/VacancyAdvicesModal/useProxyVacancyAdviceModalEvent';
import { sortBySeverity } from 'src/components/VacancyAdvicesModal/utils';

interface HideAdviceFormValues {
    vacancyId: number;
    adviceCode: VacancyAdvice;
}

declare global {
    interface FetcherPostApi {
        [HIDE_ADVICE_URL]: {
            queryParams: void;
            body: HideAdviceFormValues;
            response: void;
        };
    }
}

const VacancyAdviceModalContent: FC = () => {
    const dispatch = useDispatch();
    const {
        vacancy,
        rating: selectedRating,
        adviceWithStatistics,
        compensation,
        marketRange,
        salaryRange,
        numberOfSimilarVacancies,
    } = useSelector((state) => state.vacancyAdvicesModal);
    const { vacancyId, vacancyName } = vacancy;

    const formValues = useRef<FormValues>(DEFAULT_FORM_VALUES);

    const adviceSorted = sortBySeverity(adviceWithStatistics);

    const hideAdviceHandler = (adviceName: VacancyAdvice) => {
        const newAdviceWithStatistics = adviceWithStatistics.filter((item) => item.advice !== adviceName);
        const setVacancyAdvicesEvent = new CustomEvent(VACANCY_ADVICE_MODAL_COMPONENT_EVENT, {
            detail: {
                type: VacancyAdviceModalEventType.SetAdvice,
                vacancyId,
                adviceWithStatistics: newAdviceWithStatistics,
            },
        });
        window.dispatchEvent(setVacancyAdvicesEvent);

        const formData = prepareFormData({
            vacancyId,
            adviceCode: adviceName,
        });
        fetcher.post(HIDE_ADVICE_URL, formData).catch(console.error);

        dispatch(setAdvicesVacancyAdvicesModal(newAdviceWithStatistics));
    };

    const onSendFeedback = useCallback(() => {
        Analytics.sendHHEvent('employer_vacancies_advice_feedback_sent', {
            vacancyId,
            rating: selectedRating,
            reasons: JSON.stringify(formValues.current.reasons),
            comment: formValues.current.comment,
        });
    }, [selectedRating, vacancyId]);

    useProxyVacancyAdviceModalEvent(onSendFeedback);

    const onSelectReason = (reasons: Reason[]) => {
        Analytics.sendHHEventButtonClick('employer_vacancies_advice_feedback_reason_item', {
            vacancyId,
            reason: JSON.stringify(reasons),
        });

        formValues.current.reasons = reasons;
    };

    const onChangeFeedbackText = (text: string) => {
        formValues.current.comment = text;
    };

    const onShownNegativeSection = useCallback(
        (element: HTMLDivElement) => {
            if (element) {
                Analytics.sendHHEventElementShown(element, {
                    name: 'employer_vacancies_advice_feedback_reason',
                    vacancyId,
                });
            }
        },
        [vacancyId]
    );

    const modalAdviceContentRef = useAnalyticsEventShownOnce({
        name: 'employer_vacancies_advice_modal_shown',
        vacancyId,
        advices: JSON.stringify(adviceWithStatistics),
    });

    const modalFeedbackContentRef = useAnalyticsEventShownOnce({
        name: 'employer_vacancies_advice_feedback_modal',
        vacancyId,
        rating: selectedRating,
    });

    return (
        <div ref={modalAdviceContentRef}>
            {selectedRating ? (
                <div ref={modalFeedbackContentRef}>
                    <FeedbackModalContent
                        selectedRating={selectedRating}
                        onSelectReason={onSelectReason}
                        onChangeText={onChangeFeedbackText}
                        onShownNegativeSection={onShownNegativeSection}
                    />
                </div>
            ) : (
                adviceSorted.map((adviceItem, ind) => (
                    <VacancyAdviceCard
                        key={adviceItem.advice}
                        adviceItem={adviceItem.advice}
                        adviceWithStatisticsItem={adviceItem}
                        numInList={ind + 1}
                        totalInList={adviceSorted.length}
                        vacancyId={vacancyId}
                        compensation={compensation}
                        marketRange={marketRange}
                        salaryRange={salaryRange}
                        hideAdviceItem={hideAdviceHandler}
                        vacancyName={vacancyName}
                        numberOfSimilarVacancies={numberOfSimilarVacancies}
                        isProxyMode
                    />
                ))
            )}
        </div>
    );
};

export default translation(VacancyAdviceModalContent);
